import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Chip,
  CircularProgress
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { useAgentChat } from "./AgentChatProvider";
import SessionSidebar from "./SessionSidebar";
import MessageList from "./MessageList";
import SessionFeedback from "./SessionFeedback";
import useStyles from "./styles";

const EMPTY_MESSAGES = [];

const DEFAULT_EXAMPLE_QUERIES = [
  "Decode this ID: kg0hxrwu5j4pbte0",
  "What enrollment is associated with this CAN (Consumer account number): 1234567890",
  "Show me the top 10 partners by application count this month",
  "How many applications were submitted in the last 7 days?",
  "What's the average approval rate by partner?"
];

export default function AgentChatUI({
  title = "Boom Agent",
  placeholder = "Ask a question about your data...",
  exampleQueries = DEFAULT_EXAMPLE_QUERIES,
  showBetaChip = false,
  emptyStateTitle = "How can I help?",
  emptyStateDescription = "I can decode obfuscated IDs, find enrollments by CAN(Consumer account number) and answer questions about your data using natural language."
}) {
  const classes = useStyles();
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  const { activeSessionId, activeSession, token, createSession, sendMessage } =
    useAgentChat();

  // Derive state from context
  const messages = activeSession?.messages || EMPTY_MESSAGES;
  const isStreaming = activeSession?.status === "streaming";
  const isLoading = activeSession?.status === "loading";
  const agentStatusUpdate = activeSession?.agentStatusUpdate || null;

  const [input, setInput] = useState("");

  // Auto-focus input when session changes
  useEffect(() => {
    if (token) inputRef.current?.focus();
  }, [activeSessionId, token]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesContainerRef.current)
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
  }, [messages]);

  const handleSend = useCallback(async () => {
    const message = input.trim();
    if (!message || isStreaming || !token) return;

    let sessionId = activeSessionId;
    if (!sessionId) {
      const newSession = await createSession();
      if (!newSession) return;
      sessionId = newSession.id;
    }

    setInput("");
    await sendMessage(sessionId, message);

    inputRef.current?.focus();
  }, [input, isStreaming, token, activeSessionId, createSession, sendMessage]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleExampleClick = (query) => {
    setInput(query);
    inputRef.current?.focus();
  };

  return (
    <Box className={classes.root}>
      <SessionSidebar />

      <Paper className={classes.chatArea}>
        <Box className={classes.chatHeader}>
          <Typography variant="h6">{title}</Typography>
          {showBetaChip && (
            <Chip
              label="Beta"
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          <SessionFeedback />
        </Box>

        <Box ref={messagesContainerRef} className={classes.messagesContainer}>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress />
            </Box>
          ) : (
            <MessageList
              classes={classes}
              messages={messages}
              isStreaming={isStreaming}
              agentStatusUpdate={agentStatusUpdate}
              exampleQueries={exampleQueries}
              emptyStateTitle={emptyStateTitle}
              emptyStateDescription={emptyStateDescription}
              onExampleClick={handleExampleClick}
            />
          )}
        </Box>

        <Box className={classes.inputArea}>
          <TextField
            inputRef={inputRef}
            className={classes.inputField}
            variant="outlined"
            size="medium"
            placeholder={placeholder}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isStreaming || !token}
            multiline
            maxRows={3}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={isStreaming || !token || !input.trim()}
          >
            {isStreaming ? <CircularProgress size={24} /> : <SendIcon />}
          </IconButton>
        </Box>

        <Box className={classes.footer}>
          <InfoOutlinedIcon fontSize="small" />
          <span>
            AI can make mistakes. Always verify important information.
          </span>
        </Box>
      </Paper>
    </Box>
  );
}
